import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { CompanyView } from '../../types/entity-types';
import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { getCountryCurrency } from '../../utils/get-country-currency';

/**
 * Custom hook to derive base currency from company data
 * @param companies - Array of company data
 * @param shouldFetchData - Whether data fetching should be active
 * @returns The base currency derived from the first company's country
 */
export const useBaseCurrency = (
  companies: CompanyView[] | undefined,
  shouldFetchData: boolean,
): SupportedCountryCurrency | undefined => {
  const baseCurrency = useMemo(() => {
    return companies?.[0] ? getCountryCurrency(companies[0].legalEntity.country) : undefined;
  }, [companies]);

  // Add consolidated debugging for base currency calculation
  useEffect(() => {
    if (shouldFetchData && companies?.[0]) {
      const companyCountry = companies[0].legalEntity.country;
      const derivedCurrency = getCountryCurrency(companyCountry);

      if (!baseCurrency) {
        Sentry.addBreadcrumb({
          category: 'data_provider',
          message: 'Unable to determine base currency despite having company data',
          level: 'error',
          data: {
            companyId: companies[0].id,
            companyCountry,
            derivedCurrency,
            getCountryCurrencyResult: derivedCurrency,
          },
        });
      }
    }

    // Add debug info when companies exist but baseCurrency is undefined
    if (shouldFetchData && companies?.length && !baseCurrency) {
      Sentry.addBreadcrumb({
        category: 'data_provider',
        message: 'Companies loaded but base currency is undefined',
        level: 'info',
        data: {
          companiesCount: companies.length,
          firstCompany: companies[0]
            ? {
                id: companies[0].id,
                country: companies[0].legalEntity?.country,
              }
            : null,
          baseCurrency,
        },
      });
    }
  }, [shouldFetchData, companies, baseCurrency]);

  return baseCurrency;
};
